import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Search, CloudUpload, FilePdf, Trash, ArrowLeft } from 'react-bootstrap-icons';
import DashboardLayout from '../components/DashboardLayout';
import UploadFileForm from '../components/UploadFileForm';
import { apiRequest } from '../utils/api';
import { resolveFileFromCandidates } from '../utils/fileUrl';
import Swal from 'sweetalert2';
import { getActiveCompanyId } from '../utils/companyContext';
import { getModulePermissions } from '../utils/permissions';
import '../assets/styles/files.css';

const FilesFormView = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { folderId } = useParams();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileNames, setFileNames] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchNotes, setSearchNotes] = useState('');
  const [searchOutletId, setSearchOutletId] = useState('');
  const [activeCompanyId, setActiveCompanyId] = useState(() => getActiveCompanyId());

  const permissions = useMemo(() => getModulePermissions('employee.files'), []);
  const canUpload = permissions.create || permissions.edit;
  const canDelete = permissions.delete;

  const normalizeDocumentGroups = useCallback((data = []) => {
    if (!Array.isArray(data)) {
      return [];
    }

    if (data.length > 0 && Array.isArray(data[0].documents)) {
      return data.map((group) => ({
        ...group,
        documents: Array.isArray(group.documents) ? group.documents : [],
      }));
    }

    const grouped = data.reduce((groups, document) => {
      const formName = document?.fileName?.name || 'Uncategorized';
      const fileNameId = document?.fileName?.id ?? null;
      const key = fileNameId ?? formName;

      if (!groups[key]) {
        groups[key] = { formName, fileNameId, documents: [] };
      }

      groups[key].documents.push(document);
      return groups;
    }, {});

    return Object.values(grouped);
  }, []);

  const loadMetadata = useCallback(async () => {
    try {
      if (!activeCompanyId) {
        setFileNames([]);
        setOutlets([]);
        return;
      }

      const params = new URLSearchParams();
      params.set('companyId', activeCompanyId);

      const [fileNameResponse, outletResponse] = await Promise.all([
        apiRequest(`/api/admin/files/names?${params.toString()}`),
        apiRequest(`/api/admin/settings/outlets?${params.toString()}`),
      ]);

      setFileNames(Array.isArray(fileNameResponse.data) ? fileNameResponse.data : []);
      setOutlets(Array.isArray(outletResponse.data) ? outletResponse.data : []);
    } catch (metadataError) {
      console.error('Failed to load file metadata:', metadataError);
    }
  }, [activeCompanyId]);

  const selectedFile = useMemo(() => {
    const matchingById = fileNames.find((file) => String(file.id) === folderId);
    const matchingByName = fileNames.find((file) => file.fileName === folderId);

    return matchingById || matchingByName || null;
  }, [fileNames, folderId]);

  const selectedFormName = useMemo(() => {
    if (selectedFile) {
      return selectedFile.fileName;
    }

    if (state?.formName) {
      return state.formName;
    }

    try {
      return decodeURIComponent(folderId);
    } catch (decodeError) {
      return folderId;
    }
  }, [folderId, selectedFile, state?.formName]);

  const fetchDocuments = useCallback(async () => {
    if (!selectedFormName || !activeCompanyId) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const params = new URLSearchParams();
      params.set('fileName', selectedFormName);

      if (searchNotes.trim()) {
        params.set('notes', searchNotes.trim());
      }

      if (searchOutletId) {
        params.set('outletId', searchOutletId);
      }

      params.set('companyId', activeCompanyId);

      const query = params.toString();
      const { data } = await apiRequest(`/api/admin/files/documents${query ? `?${query}` : ''}`);
      const groups = normalizeDocumentGroups(data);
      const matchingGroup = groups.find(
        (group) => String(group.fileNameId) === folderId || group.formName === selectedFormName,
      );

      setDocuments(matchingGroup ? matchingGroup.documents : []);
    } catch (err) {
      setError(err.message || 'Failed to load documents.');
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeCompanyId, folderId, normalizeDocumentGroups, searchNotes, searchOutletId, selectedFormName]);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchDocuments();
    }, 300);

    return () => clearTimeout(timeout);
  }, [fetchDocuments]);

  useEffect(() => {
    const handleActiveCompanyChanged = (event) => {
      const nextCompanyId = event?.detail?.id ?? getActiveCompanyId();
      setActiveCompanyId(nextCompanyId);
      setSearchOutletId('');
      setOutlets([]);
    };

    window.addEventListener('activeCompanyChanged', handleActiveCompanyChanged);

    return () => {
      window.removeEventListener('activeCompanyChanged', handleActiveCompanyChanged);
    };
  }, []);

  const handleDocumentUploaded = useCallback(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDeleteDocument = useCallback(
    async (documentId) => {
      if (!canDelete) {
        Swal.fire('Access denied', 'You do not have permission to delete files.', 'error');
        return;
      }

      const result = await Swal.fire({
        title: 'Delete file?',
        text: 'Are you sure you want to delete this file? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, delete',
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        await apiRequest(`/api/admin/files/documents/${documentId}`, { method: 'DELETE' });
        setDocuments((prev) => prev.filter((document) => document.id !== documentId));
      } catch (deleteError) {
        window.alert(deleteError.message || 'Failed to delete the file.');
      }
    },
    [],
  );

  const sortedOutlets = useMemo(() => {
    return [...outlets].sort((a, b) => a.branchName.localeCompare(b.branchName));
  }, [outlets]);

  const actionButtonGroupClass = [
    'd-flex',
    'flex-column',
    'flex-md-row',
    'justify-content-md-end',
    'align-items-stretch',
    'align-items-md-center',
    'gap-3',
    'mb-4',
  ].join(' ');

  return (
    <DashboardLayout>
      <div className="files-page container-fluid py-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <button
              type="button"
              className="btn btn-link text-decoration-none ps-0"
              onClick={() => navigate('/files')}
            >
              <ArrowLeft className="me-2" /> Back to folders
            </button>
            <h4 className="mb-0 fw-semibold">{selectedFormName || 'Form'}</h4>
          </div>
          <div className={actionButtonGroupClass}>
            {canUpload && (
              <button
                className="files-pill-button d-flex align-items-center justify-content-center"
                onClick={() => setShowUploadModal(true)}
              >
                <CloudUpload className="me-2" />
                Upload New File
              </button>
            )}
          </div>
        </div>

        <div className="files-filters shadow-sm mb-4">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label text-muted small fw-semibold text-uppercase">Search Notes</label>
              <div className="files-search-control">
                <Search className="search-icon" size={18} />
                <input
                  type="text"
                  className="form-control files-input"
                  placeholder="Search Notes"
                  value={searchNotes}
                  onChange={(e) => setSearchNotes(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <label className="form-label text-muted small fw-semibold text-uppercase">Outlet</label>
              <select
                className="form-select files-select"
                value={searchOutletId}
                onChange={(event) => setSearchOutletId(event.target.value)}
              >
                <option value="">All Outlets</option>
                {sortedOutlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.branchName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="card files-table-card shadow-sm">
          <div className="card-body">
            {isLoading ? (
              <div className="py-5 text-center">
                <Spinner animation="border" role="status" />
              </div>
            ) : error ? (
              <div className="py-5 px-3">
                <Alert variant="danger" className="mb-0">
                  {error}
                </Alert>
              </div>
            ) : documents.length === 0 ? (
              <div className="files-empty-state text-center py-5">
                <div className="files-empty-icon d-inline-flex align-items-center justify-content-center mb-3">
                  <FilePdf size={28} />
                </div>
                <p className="mb-1 fw-semibold">No documents found.</p>
                <small className="text-muted">Try adjusting filters or upload a new file.</small>
              </div>
            ) : (
              <div className="files-folder-body">
                {documents.map((document) => (
                  <div className="files-folder-file" key={document.id}>
                    <div className="files-file-main">
                      <div className="files-file-icon">
                        <FilePdf size={20} />
                      </div>
                      <div className="files-file-info">
                        <p className="files-file-name mb-1">{document.originalName || 'Document'}</p>
                        <p className="files-file-notes text-muted mb-0">
                          {document.notes || 'No notes provided'}
                        </p>
                      </div>
                      <div className="files-file-actions d-flex align-items-center gap-2">
                        {resolveFileFromCandidates(document.fileUrl, document.filePath) ? (
                          <a
                            href={resolveFileFromCandidates(document.fileUrl, document.filePath)}
                            className="btn btn-icon"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="View document"
                          >
                            <FilePdf size={18} />
                          </a>
                        ) : (
                          <span className="text-muted small">N/A</span>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="btn btn-icon"
                            onClick={() => handleDeleteDocument(document.id)}
                            aria-label="Delete document"
                          >
                            <Trash size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="files-file-footer d-flex flex-wrap gap-3">
                      <span className="text-muted small">
                        Outlet: {document.outlet?.name || document.outlet?.branchName || '-'}
                      </span>
                      <span className="text-muted small">
                        Uploaded: {new Date(document.createdAt).toLocaleString()}
                      </span>
                      {document.uploader?.name && (
                        <span className="text-muted small">By {document.uploader.name}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <UploadFileForm
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          fileNames={fileNames}
          outlets={outlets}
          onUploaded={handleDocumentUploaded}
        />
      </div>
    </DashboardLayout>
  );
};

export default FilesFormView;
